/* eslint-disable react/jsx-key */
import { useState, useEffect, useMemo } from 'react';
import { Button, MultiSelect, Select, Text, Input } from '@mantine/core';
import { List } from '@/components/List/List';
import { SortHeader } from '@/components/List/Header/SortHeader';
import { TextHeader } from '@/components/List/Header/TextHeader';
import { AlignedCell } from '@/components/List/Cell/AlignedCell';
import { ClampedTextCell } from '@/components/List/Cell/ClampedTextCell';
import { BooleanBadge } from '@/components/Badge/BooleanBadge';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { postFetcher, getFetcher } from '@/api/fetchers';
import { createKey, readKey } from '@/api/params';
import { type Services } from '@/schemas/API/GET/service/response';
import type { ClientsRequest } from '@/schemas/API/GET/client/request';
import type { Client } from '@/schemas/API/GET/client/response';
import { type Tags } from '@/schemas/API/GET/tag/response';
import { type Mapping } from '@/schemas/API/GET/mapping/response';
import { MappingToComboBoxItem } from '@/schemas/converters/mappingShemasConverter';
import { toNumber } from 'lodash';
import { formatDate } from '@/utils/Date';
import useDebounce from '@/hooks/client/useDebounce';
import { useExportStatus } from '@/hooks/export/useExportStatus';
import { downloadFileWithBearerToken} from '@/utils/functions';

const ClientNameCell = (client: Client) => (
  <ClampedTextCell>
    {client.firstName} {client.lastName}
  </ClampedTextCell>
);

const ClientSex = (sexMap: Record<string, string>) => {
  const Cell = (client: Client) => <Text>{sexMap?.[client.sex]}</Text>;
  return Cell;
}

const ClientAddressCell = (client: Client) => (
  <ClampedTextCell>
    {client.permAddress.street} {client.permAddress.city}
  </ClampedTextCell>
);

const ClientNextRevDateCell = (client: Client) => (
  <AlignedCell align="center">
    <Text>
      {client.closestNextRevDate
        ? formatDate(client.closestNextRevDate)
        : 'Neurčeno'}
    </Text>
  </AlignedCell>
);

const ClientBirthdateCell = (client: Client) => (
  <Text>{formatDate(client.birthdate)}</Text>
);

const ClientServicesCell = (availableServices: Services | undefined) => {
  const Cell = (client: Client) =>  {
    return (
      <ClampedTextCell>
        {
          client.carePlans
            .map(plan => availableServices
                        ?.find(service => service.id === plan.serviceId))
            .map(service => service?.name)
            .join(', ')
        }
      </ClampedTextCell>
    )
  }
  return Cell;
};

const ClientActiveCell = (client: Client) => (
  <AlignedCell align="center">
    <BooleanBadge
      failLabel="Neaktivní"
      successLabel="Aktivní"
      successCond={() => client.active} />
  </AlignedCell>
);

export const ClientListPage = () => {
  const navigate = useNavigate();
  const [requestParams, setRequestParams] = useState<ClientsRequest>({
    tag: [],
    page: 1,
    sort: [],
    size: 10,
    query: '',
  });

  const [isExporting, setIsExporting] = useState(false);
  const debouncedRequestParams = useDebounce(requestParams, 300);

  useEffect(() => {
    const queryParams = readKey(window.location.search);
    if (queryParams) setRequestParams(prev => ({ ...prev, ...queryParams }));
  }, []);

  useEffect(() => {
    navigate(createKey(debouncedRequestParams), { replace: true });
  }, [debouncedRequestParams, navigate]);

  const clientsKey = useMemo(
    () => `/clients${createKey(debouncedRequestParams)}`,
    [debouncedRequestParams]
  );

  const { data: clients } = useSWR(clientsKey, getFetcher);
  const { data: availableServices } = useSWR<Services>('services', getFetcher);
  const { data: availableTags } = useSWR<Tags>('tags', getFetcher);
  const { data: sexMap } = useSWR<Mapping>('mappings/sexes', getFetcher);
  const { checkExportStatus } = useExportStatus();

    const setRequestParam = <K extends keyof ClientsRequest>(
    fieldName: K,
    value: ClientsRequest[K] | null
  ): void => {
    setRequestParams(prevParams => {
      return { ...prevParams, [fieldName]: value };
    });
  };

    const handleClientsExport = async () => {
        try {
            setIsExporting(true);
            const currentRequestParams = { ...requestParams };
            const requestData = {
                filter: {
                    query: currentRequestParams.query,
                    active: currentRequestParams.active,
                    serv: currentRequestParams.serv,
                    sex: currentRequestParams.sex,
                    tag: currentRequestParams.tag,
                },
            };
            const { exportId } = await postFetcher('/exports/clients', requestData);
            const exportStatus = await checkExportStatus(exportId);
            if (exportStatus?.state === 'FINISHED' && exportStatus?.url) {
                await downloadFileWithBearerToken(exportStatus.url );
            } else {
                console.error('Export failed or no download URL available.');
            }
        } catch (error) {
            console.error('Error exporting clients:', error);
        } finally {
            setIsExporting(false);
        }
    };

  const selectors = useMemo(
    () => [
      <Input.Wrapper label="Vyhledat">
        <Input
          placeholder="např: Václav"
          value={requestParams?.query}
          onChange={e => setRequestParam('query', e.currentTarget.value)}
        />
      </Input.Wrapper>,
      <Select
        label="Služby"
        placeholder="Všechny"
        clearable
        value={String(requestParams.serv)}
        onChange={e => setRequestParam('serv', e ? Number(e) : null)}
        data={availableServices?.map(service => ({
          value: service.id.toString(),
          label: service.name,
        }))}
        data-testid="services-search-input"
      />,
      <MultiSelect
        label="Tagy"
        value={requestParams.tag?.map(tag => tag.toString())}
        onChange={selected =>
          setRequestParam(
            'tag',
            selected.map(s => Number(s))
          )
        }
        data={availableTags?.content.map(tag => ({
          value: tag.id.toString(),
          label: tag.name,
        }))}
        clearable
        data-testid="tags-search-input"
      />,
      <Select
        placeholder="Všechny"
        label="Stav uživatele"
        value={
          requestParams.active === null
            ? null
            : String(toNumber(requestParams.active))
        }
        clearable
        onChange={e =>
          setRequestParam('active', e === null ? null : Boolean(toNumber(e)))
        }
        data={[
          { value: '1', label: 'Aktivní' },
          { value: '0', label: 'Neaktivní' },
        ]}
        data-testid="state-search-input"
      />,
      <Select
        label="Pohlaví"
        placeholder="Všechny"
        clearable
        value={requestParams.sex}
        onChange={e => setRequestParam('sex', e)}
        data={sexMap ? MappingToComboBoxItem(sexMap) : []}
        data-testid="sex-search-input"
      />,
    ],
    [requestParams, availableServices, availableTags, setRequestParam]
  );

  return (
    <div className="h-full p-8 overflow-auto">
      <List
        name="Klienti"
        pagination={{
          page: Number(requestParams.page),
          setPage: page => setRequestParam('page', page),
          pageTotal: clients?.totalPages ?? 1,
        }}
        loading={!availableServices || !availableTags || !clients}
        perPageSelector={{
          label: 'Klientů na stránce',
          value: requestParams.size ?? 0,
          setter: v => setRequestParam('size', v),
        }}

        selectors={selectors}
        controls={[
            <Button variant="light"
                key="export-excel-button"
                onClick={
                    handleClientsExport
                }
                disabled={isExporting}
            >
                {isExporting ? 'Exporting...' : 'Export'}
            </Button>,
            <Button
            onClick={() => navigate('/clients/new-client')}
            data-testid="new-client-button"
          >
            Nový klient
          </Button>
        ]}
        columns={[
          <SortHeader
            label="Jméno a příjmení"
            fields={['firstName', 'lastName']}
            callback={sort => setRequestParam('sort', sort)}
            currentSort={requestParams.sort ?? []}
          />,
          <TextHeader>Pohlaví</TextHeader>,
          <TextHeader>Bydliště</TextHeader>,
          <SortHeader
            align="center"
            label="Datum příští revize"
            fields={['closestNextRevDate']}
            callback={sort => setRequestParam('sort', sort)}
            currentSort={requestParams.sort ?? []}
          />,
          <SortHeader
            label="Datum narození"
            fields={['birthdate']}
            callback={sort => setRequestParam('sort', sort)}
            currentSort={requestParams.sort ?? []}
          />,
          <TextHeader>Služby</TextHeader>,
          <TextHeader align="center">Aktivnost účtu</TextHeader>,
        ]}
        content={{
          items: clients?.content ?? [],
          onClick: (client: Client) =>
            navigate(`/clients/${client.id}/personal`),
          renders: [
            ClientNameCell,
            ClientSex(sexMap ?? {}),
            ClientAddressCell,
            ClientNextRevDateCell,
            ClientBirthdateCell,
            ClientServicesCell(availableServices),
            ClientActiveCell
          ],
        }}
      />
    </div>
  );
};
